// src/services/eventPhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");

// 🔹 Local (sharp + WebP ≤1600) — routes non /api
const eventsLocalBase = `${baseUrl}/events`;

// 🔹 Rétro-compat Cloudinary (si encore utilisé) — routes /api
const eventsApiBase = `${baseUrl}/api/events`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace la photo d'un Événement (local sharp).
 * - Côté serveur : MASTER + DISPLAY -1600 (fit: inside), auto-rotate EXIF.
 * - format (webp|jpg|png) passé en querystring — défaut "webp".
 * - Retour contrôleur : { id, photo_url } (chemin /uploads/...)
 *
 * @param {number|string} eventId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<{id:number|string, photo_url:string}>}
 */
export const uploadEventPhoto = async (eventId, file, options = {}) => {
  if (!eventId) throw new Error("eventId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Côté serveur on a memoryUpload.single("file")
  form.append("file", file);

  try {
    const { data } = await axios.post(
      `${eventsLocalBase}/${encodeURIComponent(
        eventId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // axios gère le Content-Type multipart
        },
        onUploadProgress,
      }
    );
    return data; // { id, photo_url }
  } catch (error) {
    console.error("[uploadEventPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome la photo d'un Événement (local sharp).
 * - Si 'src' est omis, le serveur dérive le MASTER depuis photo_url actuelle.
 * - x,y,width,height en pixels par rapport au MASTER.
 * - outW/outH si tu veux forcer un format cover (ex: 1200x630),
 *   sinon la sortie est ≤1600 (fit: inside).
 * - Retour : { id, photo_url } (nouvelle display -1600).
 *
 * @param {number|string} eventId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format]
 * @param {string} [crop.src]     - /uploads/... si tu veux recadrer une autre source
 * @param {number} [crop.outW]    - largeur de sortie (cover)
 * @param {number} [crop.outH]    - hauteur de sortie (cover)
 * @returns {Promise<{id:number|string, photo_url:string}>}
 */
export const cropEventPhoto = async (eventId, crop = {}) => {
  if (!eventId) throw new Error("eventId requis");

  const { x, y, width, height, format, src, outW, outH } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
  }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;
  if (typeof outW === "number") payload.outW = outW;
  if (typeof outH === "number") payload.outH = outH;

  try {
    const { data } = await axios.post(
      `${eventsLocalBase}/${encodeURIComponent(eventId)}/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );
    return data; // { id, photo_url }
  } catch (error) {
    console.error("[cropEventPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * (Optionnel) Upload via Cloudinary — rétro-compat.
 * - Route : PATCH /api/events/:id/photo
 * - Champ: "image"
 * - Retour: dépend de eventController.updateEventPhoto
 *
 * @param {number|string} eventId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<any>}
 */
export const uploadEventPhotoCloudinary = async (
  eventId,
  file,
  options = {}
) => {
  if (!eventId) throw new Error("eventId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Route Cloudinary: upload("events").single("image")
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${eventsApiBase}/${encodeURIComponent(
        eventId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[uploadEventPhotoCloudinary] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si commence par /uploads/ => préfixe avec baseUrl (API)
 * - Garde tel quel si https://, data:, blob:, ou un asset front (/img/…)
 *
 * @param {string} u
 * @returns {string}
 */
export const toAbsoluteEventPhotoUrl = (u = "") => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  return `${baseUrl}${s}`;
};
